import type { LightingManager } from '../lighting-manager.class';
import { DEFAULT_LIGHTING_PRESET } from '../types';

export type ApplyCustomSkyBrightnessParams = {
	/** Average brightness of the custom sky image (0 = dark, 1 = bright) */
	brightness: number;
};

/**
 * Adjusts scene lighting intensities to match the perceived brightness of
 * a custom sky image.
 *
 * Maps the 0-1 brightness range onto multipliers for each light source so
 * that a dark/evening image dims the scene and a bright/daytime image keeps
 * the scene fully lit.
 *
 * Brightness mapping:
 *   0.0  ->  night-like scene (~30 % of default intensities)
 *   0.5  ->  neutral / overcast (~85 % of defaults)
 *   1.0  ->  bright daylight (100 % of defaults)
 */
export const applyCustomSkyBrightnessCfn = (self: LightingManager, params: ApplyCustomSkyBrightnessParams): void => {
	const { brightness } = params;

	// Clamp brightness to 0-1
	const b = Math.max(0, Math.min(1, brightness));

	// Map brightness to intensity multipliers.
	// We use a curve that keeps the scene well-lit for bright images and
	// progressively dims it for darker images, but never goes fully black.
	// multiplier = minFloor + (1 - minFloor) * brightness^curve
	const minFloor = 0.3;
	const curve = 0.6; // slightly sub-linear so mid-tones stay reasonably lit
	const multiplier = minFloor + (1 - minFloor) * Math.pow(b, curve);

	const ambientLight = self.internalAmbientLight;
	if (ambientLight) {
		ambientLight.intensity = DEFAULT_LIGHTING_PRESET.ambient.intensity * multiplier;
	}

	const directionalLight = self.internalDirectionalLight;
	if (directionalLight) {
		directionalLight.intensity = DEFAULT_LIGHTING_PRESET.directional.intensity * multiplier;
	}

	const hemisphereLight = self.internalHemisphereLight;
	if (hemisphereLight) {
		hemisphereLight.intensity = DEFAULT_LIGHTING_PRESET.hemisphere.intensity * multiplier;
	}

	const { scene } = self.internalConfig;
	if (scene) {
		// Scale IBL contribution from scene.environment to match custom sky brightness.
		// Without this, the fixed HDR environment map (used for reflections) provides
		// dominant Image-Based Lighting that overwhelms the dimmed discrete lights.
		scene.environmentIntensity = multiplier;

		// Scale the sky background texture brightness so it visually matches
		// the adjusted scene lighting.
		scene.backgroundIntensity = multiplier;
	}
};
